import React, { PropsWithChildren, useEffect, useRef } from 'react';

import cn from 'classnames';

import { utilityCSSVariables } from '../../../css/utils';
import { P4 } from '../Typography';

import styles from './Input.module.css';
import type { InputWrapperProps } from './types';
import { getInputMainTypography } from './utils';

const getTypographyColor = (
  state: InputWrapperProps['state'],
  disabled: InputWrapperProps['disabled']
) => {
  if (disabled) {
    return 'disabled';
  }

  if (state === 'critical') {
    return 'critical';
  }

  return undefined;
};

const getIconSize = (size: InputWrapperProps['size']) => {
  switch (size) {
    case 'l':
      return 18;
    case 's':
    case 's-condensed':
    case 'm':
    default:
      return 16;
  }
};

/**
 * Get the input element from the event.currentTarget
 * @param eventCurrentTarget - The current target of the event
 * @returns The input element or null if the wrapper is not an HTMLDivElement or the input is not found
 */
const getInput = (eventCurrentTarget: HTMLDivElement | EventTarget | null) => {
  if (!eventCurrentTarget || !(eventCurrentTarget instanceof HTMLDivElement)) {
    return null;
  }

  return eventCurrentTarget.querySelector<HTMLInputElement>(
    `input.${styles['input-component']}`
  );
};

export const RootWrapper: React.FC<PropsWithChildren<InputWrapperProps>> = ({
  children,
  dataTestId,
  size = 'm',
  disabled = false,
  id,
  state = 'rest',
  label,
  disclaimer,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  handleWheelRef,
  className,
}) => {
  const LabelTypography = getInputMainTypography(size);
  const iconSize = getIconSize(size);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /**
     * For some inputs we need to change the value on wheeling. Since input itself is much smaller
     * than the wrapper, we need to handle the wheel event on the wrapper and pass it to the input
     *
     * Only handle wheel if the wheel event is not on the input (as if the input is a target, the wheel event is handled by the browser)
     */
    const handleWheel = (e: WheelEvent) => {
      const input = getInput(e.currentTarget);
      if (input && e.target !== input) {
        handleWheelRef?.current?.(e, input);
      }
    };

    const inputWrapper = inputWrapperRef.current;
    if (!inputWrapper || !handleWheelRef?.current) {
      return;
    }
    inputWrapper.addEventListener('wheel', handleWheel, {
      passive: false,
    });

    return () => {
      inputWrapper.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheelRef]);

  /**
   * Since input itself is much smaller than the wrapper, we need to focus it (if it's not already focused)
   * by clicking on the wrapper elements (like icons)
   */
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const input = getInput(e.currentTarget);
    if (input && document.activeElement !== input) {
      input.focus();
    }
  };

  const InputTypography = getInputMainTypography(size);

  return (
    <div className={styles['input-root-wrapper']} data-testid={dataTestId}>
      {label && (
        <label htmlFor={id}>
          <LabelTypography
            as="span"
            color={getTypographyColor(state, disabled) ?? 'default'}
          >
            {label}
          </LabelTypography>
        </label>
      )}
      <InputTypography as="span" color="inherit">
        <div
          ref={inputWrapperRef}
          className={cn(
            styles['input-root'],
            styles[`size-${size}`],
            className
          )}
          role="group"
          onClick={handleClick}
        >
          {LeftIcon && (
            <span aria-hidden="true" className={styles['left-icon']}>
              <LeftIcon
                color="currentColor"
                size={iconSize}
                transitionDuration={utilityCSSVariables.transition}
              />
            </span>
          )}
          {children}
          {RightIcon && (
            <span aria-hidden="true" className={styles['right-icon']}>
              <RightIcon
                color="currentColor"
                size={iconSize}
                transitionDuration={utilityCSSVariables.transition}
              />
            </span>
          )}
        </div>
      </InputTypography>
      {disclaimer && (
        <P4 color={getTypographyColor(state, disabled) ?? 'secondary'}>
          {disclaimer}
        </P4>
      )}
    </div>
  );
};
